/**
 * Templates Effects
 * Handles side effects for template actions
 */
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TemplateService } from '../services/template.service';
import { NotificationService } from '../../../core/services/notification.service';
import * as TemplatesActions from './templates.actions';

@Injectable()
export class TemplatesEffects {
  private actions$ = inject(Actions);
  private templateService = inject(TemplateService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);

  // Load Templates
  loadTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplatesActions.loadTemplates),
      switchMap(({ filters }) =>
        this.templateService.getTemplates(filters).pipe(
          map((response) => TemplatesActions.loadTemplatesSuccess({ response })),
          catchError((error) => {
            const errorMessage = error.error?.detail || 'Failed to load templates';
            this.notificationService.showError(errorMessage);
            return of(TemplatesActions.loadTemplatesFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  // Load Public Templates
  loadPublicTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplatesActions.loadPublicTemplates),
      switchMap(({ filters }) =>
        this.templateService.getPublicTemplates(filters).pipe(
          map((response) => TemplatesActions.loadPublicTemplatesSuccess({ response })),
          catchError((error) => {
            const errorMessage = error.error?.detail || 'Failed to load public templates';
            this.notificationService.showError(errorMessage);
            return of(TemplatesActions.loadPublicTemplatesFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  // Load Single Template
  loadTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplatesActions.loadTemplate),
      switchMap(({ id }) =>
        this.templateService.getTemplate(id).pipe(
          map((template) => TemplatesActions.loadTemplateSuccess({ template })),
          catchError((error) => {
            const errorMessage = error.error?.detail || 'Failed to load template';
            this.notificationService.showError(errorMessage);
            return of(TemplatesActions.loadTemplateFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  // Create Template
  createTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplatesActions.createTemplate),
      switchMap(({ request }) =>
        this.templateService.createTemplate(request).pipe(
          map((template) => {
            this.notificationService.showSuccess('Template created successfully');
            return TemplatesActions.createTemplateSuccess({ template });
          }),
          catchError((error) => {
            const errorMessage = error.error?.detail || 'Failed to create template';
            this.notificationService.showError(errorMessage);
            return of(TemplatesActions.createTemplateFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  // Navigate after create success
  createTemplateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TemplatesActions.createTemplateSuccess),
        tap(() => {
          this.router.navigate(['/templates']);
        })
      ),
    { dispatch: false }
  );

  // Update Template
  updateTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplatesActions.updateTemplate),
      switchMap(({ id, request }) =>
        this.templateService.updateTemplate(id, request).pipe(
          map((template) => {
            this.notificationService.showSuccess('Template updated successfully');
            return TemplatesActions.updateTemplateSuccess({ template });
          }),
          catchError((error) => {
            const errorMessage = error.error?.detail || 'Failed to update template';
            this.notificationService.showError(errorMessage);
            return of(TemplatesActions.updateTemplateFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  // Delete Template
  deleteTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplatesActions.deleteTemplate),
      switchMap(({ id }) =>
        this.templateService.deleteTemplate(id).pipe(
          map(() => {
            this.notificationService.showSuccess('Template deleted successfully');
            return TemplatesActions.deleteTemplateSuccess({ id });
          }),
          catchError((error) => {
            const errorMessage = error.error?.detail || 'Failed to delete template';
            this.notificationService.showError(errorMessage);
            return of(TemplatesActions.deleteTemplateFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  // Navigate after delete success
  deleteTemplateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TemplatesActions.deleteTemplateSuccess),
        tap(() => {
          this.router.navigate(['/templates']);
        })
      ),
    { dispatch: false }
  );

  // Clone Template
  cloneTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplatesActions.cloneTemplate),
      switchMap(({ id, request }) =>
        this.templateService.cloneTemplate(id, request).pipe(
          map((template) => {
            this.notificationService.showSuccess('Template cloned successfully');
            return TemplatesActions.cloneTemplateSuccess({ template });
          }),
          catchError((error) => {
            const errorMessage = error.error?.detail || 'Failed to clone template';
            this.notificationService.showError(errorMessage);
            return of(TemplatesActions.cloneTemplateFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  // Navigate after clone success
  cloneTemplateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TemplatesActions.cloneTemplateSuccess),
        tap(({ template }) => {
          this.router.navigate(['/templates', 'edit', template.id]);
        })
      ),
    { dispatch: false }
  );

  // Test Template
  testTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplatesActions.testTemplate),
      switchMap(({ id, file }) =>
        this.templateService.testTemplate(id, file).pipe(
          map((result) => {
            this.notificationService.showSuccess('Template test completed');
            return TemplatesActions.testTemplateSuccess({ result });
          }),
          catchError((error) => {
            const errorMessage = error.error?.detail || 'Failed to test template';
            this.notificationService.showError(errorMessage);
            return of(TemplatesActions.testTemplateFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  // Reload templates after create/update/delete
  reloadTemplatesAfterMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        TemplatesActions.createTemplateSuccess,
        TemplatesActions.updateTemplateSuccess,
        TemplatesActions.deleteTemplateSuccess
      ),
      map(() => TemplatesActions.loadTemplates({ filters: {} }))
    )
  );

  // ============================================================
  // Template Selection Effects (for API Usage)
  // ============================================================

  // Load Enabled Templates
  loadEnabledTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplatesActions.loadEnabledTemplates),
      switchMap(() =>
        this.templateService.getEnabledTemplates().pipe(
          map((response) => TemplatesActions.loadEnabledTemplatesSuccess({ response })),
          catchError((error) => {
            const errorMessage = error.error?.detail || 'Failed to load enabled templates';
            this.notificationService.showError(errorMessage);
            return of(TemplatesActions.loadEnabledTemplatesFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  // Enable Templates
  enableTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplatesActions.enableTemplates),
      switchMap(({ request }) =>
        this.templateService.enableTemplates(request).pipe(
          map((response) => {
            this.notificationService.showSuccess('Templates enabled for API usage');
            return TemplatesActions.enableTemplatesSuccess({ response });
          }),
          catchError((error) => {
            const errorMessage = error.error?.detail || 'Failed to enable templates';
            this.notificationService.showError(errorMessage);
            return of(TemplatesActions.enableTemplatesFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  // Disable Templates
  disableTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplatesActions.disableTemplates),
      switchMap(({ request }) =>
        this.templateService.disableTemplates(request).pipe(
          map((response) => {
            this.notificationService.showSuccess('Templates disabled from API usage');
            return TemplatesActions.disableTemplatesSuccess({ response });
          }),
          catchError((error) => {
            const errorMessage = error.error?.detail || 'Failed to disable templates';
            this.notificationService.showError(errorMessage);
            return of(TemplatesActions.disableTemplatesFailure({ error: errorMessage }));
          })
        )
      )
    )
  );
}

